import { useState, useRef, useEffect } from 'react';
import uuid from 'react-uuid';
import t from 'react-translate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';

import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import SetUpBasics from './set-up-basics';
import SetUpInstruction from './set-up-instructions';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import modalStyles from './styles';

export enum AIConfigStep {
  SET_UP_BASICS = 1,
  SET_UP_INSTRUCTIONS = 2,
}
export enum AIPersonalities {
  NEUTRAL = 'Neutral',
  IRRITABLE = 'Irritable',
  DISINTERESTED = 'Disinterested',
}

const AiRolePlayConfigModal = (props) => {
  const { onConfirm, forwardOnModalClose } = props;
  const [step, setStep] = useState(AIConfigStep.SET_UP_BASICS);
  const { createAiRolePlayValidationSchema } = useAiRolePlayHelper();
  const dispatch = useDispatch();
  const forwardOnModalCloseRef = useRef(forwardOnModalClose);
  forwardOnModalCloseRef.current = forwardOnModalClose;

  const getDefaultObjectives = (count: number) => Array.from(
    { length: count },
    () => ({ id: uuid(), text: '' }),
  );

  const defaultValues = {
    title: '',
    description: '',
    learnerRole: '',
    aiCharacterRole: '',
    aiCharacterName: '',
    context: '',
    aiCharacterPersonality: AIPersonalities.NEUTRAL,
    requiredAiLearnerGoal: { text: '' },
    aiCharacterObjections: getDefaultObjectives(3),
    aiLearnerGoals: getDefaultObjectives(2),
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(createAiRolePlayValidationSchema),
  });

  const { formState } = methods;
  const { isDirty } = formState;

  useEffect(() => {
    forwardOnModalCloseRef.current((e: Event, closeModal: Function) => {
      if (isDirty) {
        e.preventDefault();

        dispatch(openConfirmationDialog({
          onConfirm: () => closeModal(),
          cancelText: t.FORM.CANCEL(),
          confirmText: t.FORM.YES_SURE(),
          title: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.TITLE(),
          bodyText: t.FORM.UNSAVED_CHANGES.CLOSE_WINDOW.DESCRIPTION(),
        }));
      }
    });
  }, [isDirty, dispatch]);

  const getForm = () => {
    switch (step) {
      case AIConfigStep.SET_UP_BASICS:
        return <SetUpBasics setStep={setStep} />;
      case AIConfigStep.SET_UP_INSTRUCTIONS:
        return <SetUpInstruction onSubmit={onConfirm} setStep={setStep} />;
      default:
        return <SetUpBasics setStep={setStep} />;
    }
  };

  return (
    <div css={modalStyles} className='pt-4'>
      <div className='d-flex py-4 px-8 align-items-center'>
        <div className='d-flex mr-auto align-items-center'>
          <div className={`${step === 1 ? 'active-step' : ''} bg-gray-4 step-number text-small text-white bold d-flex align-items-center justify-content-center`}>
            1
          </div>
          <p className={`${step === 1 ? 'text-black' : 'text-gray-2'} mb-0 bold ml-2 d-flex align-items-center text-large-body`}>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.STEP_1()}
          </p>
        </div>
        <hr className='flex-grow-1 mx-2 bg-gray-5' />
        <div className='d-flex ml-auto align-items-center'>
          <div className={`${step === 2 ? 'active-step' : ''} bg-gray-4 step-number text-small text-white bold d-flex align-items-center justify-content-center`}>
            2
          </div>
          <p className={`${step === 2 ? 'text-black' : 'text-gray-2'} mb-0 bold ml-2 d-flex align-items-center text-large-body`}>
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.STEP_2()}
          </p>
        </div>
      </div>
      <FormProvider {...methods}>
        <form>
          {getForm()}
        </form>
      </FormProvider>
    </div>
  );
};

export default AiRolePlayConfigModal;
